import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Pencil, Plus, Save, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { Markdown } from "@/components/Bits";

const EMPTY = { uvp: "", not_for: "", emotion: "", pillars: [], pillar_sheet: [] };

export const useRootsExtras = () => {
  const [x, setX] = useState(null);
  useEffect(() => { api.get("/me/roots-extras").then((r) => setX({ ...EMPTY, ...r.data })).catch(() => setX(EMPTY)); }, []);
  useEffect(() => {
    const on = (e) => setX({ ...EMPTY, ...e.detail });
    window.addEventListener("roots-extras", on);
    return () => window.removeEventListener("roots-extras", on);
  }, []);
  const save = async (patch) => {
    const r = await api.put("/me/roots-extras", { uvp: x.uvp, not_for: x.not_for, emotion: x.emotion, pillars: x.pillars, ...patch });
    window.dispatchEvent(new CustomEvent("roots-extras", { detail: r.data }));
    toast.success("Saved to your Roots Profile");
  };
  return [x, save];
};

const Box = ({ title, id, children }) => (
  <div className="rounded-xl border-2 border-dashed border-pinkb/30 bg-pinkb/5 p-4 space-y-3" data-testid={id}>
    <div className="text-xs font-mono uppercase text-pinkb">{title}</div>
    {children}
  </div>
);

const useSaver = (save) => {
  const [busy, setBusy] = useState(false);
  const run = async (patch) => { setBusy(true); try { await save(patch); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return [busy, run];
};

const SaveBtn = ({ busy, onClick, id, disabled }) => (
  <Button type="button" size="sm" onClick={onClick} disabled={busy || disabled} data-testid={id} className="rounded-full bg-navy hover:bg-navy/90 text-white">
    {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Save to my profile
  </Button>
);

export const TextExtra = ({ extras, save, field, title, placeholder, max, rows = 2, children }) => {
  const [v, setV] = useState(extras[field]);
  const [busy, run] = useSaver(save);
  const saved = extras[field];
  useEffect(() => setV(saved), [saved]);
  return (
    <Box title={title} id={`mf-editor-${field}`}>
      <Textarea value={v} onChange={(e) => setV(e.target.value.slice(0, max))} rows={rows} placeholder={placeholder} data-testid={`mf-input-${field}`} className="bg-white" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{v.length}/{max}</span>
        <div className="flex flex-wrap gap-2">
          <SaveBtn busy={busy} onClick={() => run({ [field]: v })} disabled={v === extras[field]} id={`mf-save-${field}`} />
        </div>
      </div>
      {children && children(v)}
    </Box>
  );
};

export const UvpCheck = ({ uvp }) => {
  const [busy, setBusy] = useState(false);
  const [fb, setFb] = useState("");
  const check = async () => {
    setBusy(true);
    try { const r = await api.post("/me/uvp-feedback", { uvp }); setFb(r.data.feedback); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="mf-uvp-check">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" onClick={check} disabled={busy || uvp.trim().length < 10} data-testid="mf-uvp-check-btn" className="rounded-full border-pinkb/50 text-navy">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5 text-pinkb" />}{busy ? "Checking…" : "Is it specific enough?"}
        </Button>
        <span className="text-xs text-muted-foreground">Uses one question from your allowance</span>
      </div>
      {fb && <div className="rounded-lg bg-white border border-border p-4 fade-up"><Markdown testId="mf-uvp-feedback">{fb}</Markdown></div>}
    </div>
  );
};

export const PillarsExtra = ({ extras, save, placeholder }) => {
  const [list, setList] = useState(extras.pillars.length ? extras.pillars : ["", "", ""]);
  const [busy, run] = useSaver(save);
  const savedKey = extras.pillars.join("\n");
  useEffect(() => setList(savedKey ? savedKey.split("\n") : ["", "", ""]), [savedKey]);
  const filled = list.filter((p) => p.trim()).length;
  const set = (i, val) => setList((l) => l.map((p, j) => (j === i ? val.slice(0, 120) : p)));
  return (
    <Box title="My content pillars (3–5)" id="mf-editor-pillars">
      {list.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="font-serif text-pinkb w-5">{i + 1}</span>
          <Input value={p} onChange={(e) => set(i, e.target.value)} placeholder={i === 0 ? placeholder : ""} data-testid={`mf-pillar-${i}`} className="bg-white" />
          {list.length > 3 && <button type="button" onClick={() => setList((l) => l.filter((_, j) => j !== i))} aria-label="Remove pillar" data-testid={`mf-pillar-remove-${i}`} className="text-muted-foreground hover:text-rose-600"><X className="h-4 w-4" /></button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {list.length < 5 ? <button type="button" onClick={() => setList((l) => [...l, ""])} data-testid="mf-pillar-add" className="inline-flex items-center gap-1 text-xs text-pinkb hover:underline"><Plus className="h-3.5 w-3.5" />Add a pillar</button> : <span />}
        <SaveBtn busy={busy} onClick={() => run({ pillars: list })} disabled={filled > 0 && filled < 3} id="mf-save-pillars" />
      </div>
      {filled > 0 && filled < 3 && <p className="text-xs text-muted-foreground" data-testid="mf-pillars-hint">Add at least 3 pillars to save.</p>}
      <Link to="/app/guides?tab=pillars" data-testid="mf-pillars-worksheet-link" className="inline-flex text-xs text-pinkb hover:underline">Add why each matters and post ideas in the worksheet</Link>
    </Box>
  );
};

export const FinishFoundationsCard = ({ to = "/app/foundations#marketing-foundations" }) => {
  const [x] = useRootsExtras();
  if (!x) return null;
  const done = [x.not_for, x.uvp, x.emotion].filter(Boolean).length + (x.pillars.length >= 3 ? 1 : 0);
  if (done === 4) return null;
  const Tag = to.startsWith("#") ? "a" : Link;
  return (
    <Tag {...(to.startsWith("#") ? { href: to, onClick: (e) => { e.preventDefault(); document.querySelector(to)?.scrollIntoView({ behavior: "smooth" }); } } : { to })} data-testid="finish-foundations-card" className="fade-up mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-dashed border-pinkb/40 bg-pinkb/5 p-5 transition-colors hover:border-pinkb">
      <div>
        <div className="text-xs font-mono uppercase text-pinkb">Your marketing foundations · {done}/4 done</div>
        <p className="mt-1 text-sm text-navy">{done ? "Pick up where you left off." : "Four short questions that sharpen every answer Lindsay gives you."} Who you're not for, your UVP, the feeling you want to create and your content pillars.</p>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-sm text-white"><Pencil className="h-3.5 w-3.5" />{done ? "Finish my foundations" : "Start my foundations"}</span>
    </Tag>
  );
};
